import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs from 'dayjs';
import { getDurationFromNow } from '../../utils/dateUtils';
import { COLORS, MONTHS_WITH_NUMBERS } from '../../constants/radar';
import { toLocalDate } from '../../utils/dateOnly';

const SessionInformation = ({ 
  sessionName, 
  onSessionNameChange,
  selectedMonth,
  onMonthChange,
  selectedYear,
  onYearChange,
  deadline,
  onDeadlineChange,
  allowEmployeeToAssignReviewers,
  onAllowEmployeeToAssignReviewersChange,
  readOnly,
  showOnlyDeadline = false
}) => {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear + i);
  // Get tomorrow's date as minimum date
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {showOnlyDeadline 
          ? (readOnly ? 'Deadline date:' : 'Set the deadline date for this individual assignment:')
          : (readOnly ? 'Session information:' : 'Enter the basic information for this feedback session:')
        }
      </Typography>

      <Grid container spacing={3} sx={{
        width: '100%',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: showOnlyDeadline ? '1fr' : {
          xs: '1fr',
          md: 'repeat(2, 1fr)'
        },
        gap: '24px',
        '& .MuiGrid-item': {
          padding: '0',
          width: '100%'
        },
        maxWidth: showOnlyDeadline ? '400px' : 'none'
      }}>
        {/* Left side - Session details - Hidden when showOnlyDeadline */}
        {!showOnlyDeadline && (
          <Grid item xs={12} md={6}>
          <Box sx={{ 
            p: 3, 
            border: '1px solid #E2E8F0',
            borderRadius: 2,
            backgroundColor: '#FFFFFF',
            height: '100%'
          }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#475569', mb: 2 }}>
              Session Details
            </Typography>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Session Name"
                value={sessionName}
                onChange={(e) => onSessionNameChange(e.target.value)}
                required
                placeholder="Enter session name"
                disabled={readOnly}
                InputProps={{
                  readOnly: readOnly
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E2E8F0' },
                    '&:hover fieldset': { borderColor: readOnly ? '#E2E8F0' : '#CBD5E1' },
                    '&.Mui-focused fieldset': { borderColor: readOnly ? '#E2E8F0' : '#2563EB' }
                  }
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={allowEmployeeToAssignReviewers}
                    onChange={(e) => onAllowEmployeeToAssignReviewersChange(e.target.checked)}
                    disabled={readOnly}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#2563EB',
                        '&:hover': {
                          backgroundColor: 'rgba(37, 99, 235, 0.04)',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2563EB',
                      },
                    }}
                  />
                }
                label="Allow individuals to assign reviewers"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    color: '#475569',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            <Box sx={{ 
              display: 'flex', 
              gap: 2,
              '& .MuiFormControl-root': { flex: 1 }
            }}>
              <FormControl>
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => onMonthChange(e.target.value)}
                  label="Month"
                  required
                  disabled={readOnly}
                  readOnly={readOnly}
                  sx={{ minWidth: 140 }}
                >
                  {MONTHS_WITH_NUMBERS.map((month) => (
                    <MenuItem key={month.number} value={month.number}>
                      {month.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e) => onYearChange(e.target.value)}
                  label="Year"
                  required
                  disabled={readOnly}
                  readOnly={readOnly}
                  sx={{ minWidth: 100 }}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Grid>
        )}

        {/* Right side - Deadline calendar */}
        <Grid item xs={12} md={showOnlyDeadline ? 12 : 6}>
          <Box sx={{ 
            p: 3, 
            border: '1px solid #E2E8F0',
            borderRadius: 2,
            backgroundColor: '#FFFFFF',
            height: '100%'
          }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: '#475569', mb: 2 }}>
              Deadline Date {!readOnly && '*'}
            </Typography>

            {readOnly ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                <Typography variant="h5" sx={{ fontWeight: 'medium', color: COLORS.PRIMARY }}>
                  {deadline ? toLocalDate(deadline)?.toLocaleDateString() : 'No deadline set'}
                </Typography>
              </Box>
            ) : (
              <DateCalendar
                value={deadline ? dayjs(deadline) : null} 
                onChange={(newValue) => {
                  if (newValue) {
                    const jsDate = newValue.toDate();
                    onDeadlineChange(jsDate);
                  } else {
                    onDeadlineChange('');
                  }
                }}
                minDate={dayjs(tomorrow)}
                readOnly={readOnly}
                sx={{
                  width: '100%',
                  '& .MuiPickersDay-root': {
                    '&.Mui-selected': {
                      backgroundColor: '#2563EB',
                      '&:hover': {
                        backgroundColor: '#1D4ED8',
                      }
                    },
                    '&:hover': {
                      backgroundColor: '#EFF6FF',
                    }
                  }
                }}
              />
            )}

            {deadline && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 1,
                  textAlign: 'center',
                  color: COLORS.PRIMARY,
                  fontStyle: 'italic'
                }}
              >
                {getDurationFromNow(deadline)}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SessionInformation; 